// ─────────────────────────────────────────────────────────────────────────────
// HeyWaitr+ API client
//
// Thin wrappers around the backend endpoints used by the HeyWaitr+ feature.
// Base URL + auth header follow the same conventions as the rest of the web app
// (configData.SERVER_URL + `Authorization: Bearer <token>`).
// ─────────────────────────────────────────────────────────────────────────────
import axios from 'axios';
import configData from '../config.json';

const authHeader = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

// GET /menu/discountedbyday — public; token optional (personalises nothing today
// but mirrors the mobile call). Returns { Monday: [...], ..., Sunday: [...] }.
export const fetchDiscountedByDay = async (token) => {
  const res = await axios({
    method: 'GET',
    url: `${configData.SERVER_URL}/menu/discountedbyday`,
    headers: { ...authHeader(token) },
  });
  return res.data;
};

// GET /subscription/fetch — returns the Reepay subscription object or null.
export const fetchSubscription = async (token) => {
  const res = await axios({
    method: 'GET',
    url: `${configData.SERVER_URL}/subscription/fetch`,
    headers: { ...authHeader(token) },
  });
  return res.data;
};

// GET /subscription/getSubscriptionList — available plans (public).
export const fetchSubscriptionPlans = async () => {
  const res = await axios({
    method: 'GET',
    url: `${configData.SERVER_URL}/subscription/getSubscriptionList`,
  });
  return res.data;
};

// GET /banners — public, admin-editable marketing banners. The backend swaps
// `Text` for the translation matching the accept-language header, so we pin
// 'en' (no stored 'en' translation ⇒ the base English text) on a fresh axios
// instance the global interceptor (which injects the user's language) doesn't
// touch. utils/trialPeriod.js parses the current free-trial length out of that
// English text; localisation happens client-side via translations.json.
const bannerAxios = axios.create();
export const fetchBanners = async () => {
  const res = await bannerAxios({
    method: 'GET',
    url: `${configData.SERVER_URL}/banners`,
    headers: { 'accept-language': 'en' },
  });
  // The endpoint wraps the list as { banners: [...] }, but tolerate a bare
  // array too so the parser can't silently fall back to the default trial.
  const data = res.data;
  console.log('banners response', data);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.banners)) return data.banners;
  return [];
};

// POST /subscription/create — returns a Reepay hosted-payment URL (string) the
// caller should redirect the browser to. accept_url / cancel_url are where Reepay
// sends the user back after the payment flow completes or is cancelled.
export const createSubscription = async (token, { plan, acceptUrl, cancelUrl } = {}) => {
  const res = await axios({
    method: 'POST',
    url: `${configData.SERVER_URL}/subscription/create`,
    data: { plan, accept_url: acceptUrl, cancel_url: cancelUrl },
    headers: { ...authHeader(token) },
  });
  return res.data; // hosted payment page URL string
};

// POST /subscription/cancel — cancels at period end. Returns updated subscription.
export const cancelSubscription = async (token) => {
  const res = await axios({
    method: 'POST',
    url: `${configData.SERVER_URL}/subscription/cancel`,
    headers: { ...authHeader(token) },
  });
  return res.data;
};

// POST /subscription/resume — un-cancels a subscription. Returns updated subscription.
export const resumeSubscription = async (token) => {
  const res = await axios({
    method: 'POST',
    url: `${configData.SERVER_URL}/subscription/resume`,
    headers: { ...authHeader(token) },
  });
  return res.data;
};

// GET /user/profile — used by the ?token= deep-link from the mobile app to
// rehydrate the web session. Returns the profile object (incl. isSubscribed).
export const fetchUserProfile = async (token) => {
  const res = await axios({
    method: 'GET',
    url: `${configData.SERVER_URL}/user/profile`,
    headers: { ...authHeader(token) },
  });
  return res.data;
};

// POST /user/myfavorites/fetch — the user's favourite restaurants. We only need
// the ids (to mark deal cards), so we return a string[] of restaurant ids.
export const fetchFavoriteIds = async (token) => {
  const res = await axios({
    method: 'POST',
    url: `${configData.SERVER_URL}/user/myfavorites/fetch`,
    data: { geoLocation: {}, keyword: '' },
    headers: { ...authHeader(token) },
  });
  const list = Array.isArray(res.data) ? res.data : [];
  return list
    .map((r) => r && (r.id || r._id))
    .filter(Boolean)
    .map(String);
};

// POST /user/myfavorites/toggle — add (makeFavorite=true) or remove a favourite.
export const toggleFavorite = async (restaurantId, makeFavorite, token) => {
  const res = await axios({
    method: 'POST',
    url: `${configData.SERVER_URL}/user/myfavorites/toggle`,
    data: { restaurantid: restaurantId, myfavorite: makeFavorite },
    headers: { ...authHeader(token) },
  });
  return res.data;
};
